import posthog from "posthog-js"
import { useEffect, useState } from "react"
import type { AnalyticsEventName, AnalyticsProperties } from "./events"
import {
  buildPlayerDisplayName,
  normalizeFirstName,
  normalizeLastInitial,
} from "@/lib/playerName"

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com"
export const WILD_GUESS_SOLO_FLAG_KEY = "wild_guess_solo_enabled"

let initialized = false

export function initPostHog() {
  if (initialized || typeof window === "undefined" || !POSTHOG_KEY) return
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: true,
    capture_pageleave: true,
    person_profiles: "identified_only",
    loaded: (ph) => {
      if (import.meta.env.DEV) ph.debug()
    },
  })
  initialized = true
}

export function identifyUser(
  userId: string,
  properties?: Record<string, unknown>,
) {
  initPostHog()
  if (!initialized) return
  posthog.identify(userId, properties)
}

export function resetUser() {
  if (!initialized) return
  posthog.reset()
}

function cleanProperties(properties?: AnalyticsProperties) {
  if (!properties) return undefined
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined),
  )
}

export function trackEvent(
  event: AnalyticsEventName,
  properties?: AnalyticsProperties,
) {
  initPostHog()
  if (!initialized) return
  posthog.capture(event, cleanProperties(properties))
}

export function useFeatureFlagEnabled(key: string, defaultValue = false) {
  const [enabled, setEnabled] = useState(defaultValue)

  useEffect(() => {
    initPostHog()
    if (!initialized) {
      setEnabled(defaultValue)
      return
    }

    function readFlag() {
      setEnabled(posthog.isFeatureEnabled(key) === true)
    }

    readFlag()
    const unsubscribe = posthog.onFeatureFlags(readFlag)
    posthog.reloadFeatureFlags()

    return () => {
      unsubscribe?.()
    }
  }, [defaultValue, key])

  return enabled
}

export function useWildGuessSoloEnabled() {
  return useFeatureFlagEnabled(WILD_GUESS_SOLO_FLAG_KEY, false)
}

export function usePostHogIdentify(
  user: {
    userId: string
    email: string | null
    phoneNumber?: string | null
    firstName: string | null
    lastName: string | null
  } | null,
) {
  useEffect(() => {
    initPostHog()
  }, [])

  useEffect(() => {
    if (user) {
      const firstName = normalizeFirstName(user.firstName)
      const lastInitial = normalizeLastInitial(user.lastName)
      const displayName = buildPlayerDisplayName({ firstName, lastInitial })

      identifyUser(user.userId, {
        email: user.email,
        phoneNumber: user.phoneNumber,
        firstName: firstName || undefined,
        lastInitial: lastInitial || undefined,
        displayName,
        name: displayName,
      })
    }
  }, [user])
}
